import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Loader2, ArrowDownToLine, Trophy, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Creator earnings panel — shows tip earnings (separate bucket) with a manual
 * withdraw flow to the main token balance. Hidden if the user has never
 * received a tip AND has zero earnings (keeps the page clean).
 */
export const CreatorEarningsCard = () => {
  const { token, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchEarnings = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/community/tips/earnings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    fetchEarnings();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  if (loading) return null;
  if (!data) return null;

  const earnings = data.earnings_balance || 0;
  const lifetime = data.lifetime_received || 0;
  const txs = data.transactions || [];

  // Don't show the panel if user has no history and no balance
  if (earnings === 0 && txs.length === 0) return null;

  const requested = parseInt(amount, 10) || 0;
  const valid = requested >= 1 && requested <= earnings;
  const setMax = () => setAmount(String(earnings));

  const handleWithdraw = async () => {
    if (!valid) return;
    setWithdrawing(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/community/tips/withdraw`,
        { amount: requested },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Withdrew ${requested} XEA to your balance`);
      setAmount("");
      setData((prev) => ({
        ...prev,
        earnings_balance: r.data.earnings_balance,
        tokens_balance: r.data.tokens_balance,
      }));
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed.");
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-amber-50 to-coral-500/10 border-2 border-amber-300/50 rounded-3xl p-6 shadow-ambient"
      data-testid="creator-earnings-card"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-coral-500 text-white flex items-center justify-center shadow-md">
            <Trophy className="w-7 h-7" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
              Creator earnings
            </p>
            <p className="text-3xl font-black text-ink-950" data-testid="earnings-balance">
              {earnings.toLocaleString()} <span className="text-sm font-bold text-ink-500">XEA</span>
            </p>
            <p className="text-xs text-ink-500">
              Lifetime: {lifetime.toLocaleString()} · From {txs.length} tip{txs.length === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        {/* Withdraw form */}
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="flex items-center bg-white border border-ink-200 rounded-xl overflow-hidden flex-1 md:w-64">
            <input
              type="number"
              min={1}
              max={earnings}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount to withdraw"
              data-testid="earnings-amount-input"
              className="flex-1 px-3 py-2.5 text-sm focus:outline-none bg-transparent"
              disabled={earnings === 0}
            />
            <button
              type="button"
              onClick={setMax}
              data-testid="earnings-max-btn"
              disabled={earnings === 0}
              className="px-3 py-2.5 text-xs font-bold text-coral-600 border-l border-ink-200 hover:bg-coral-500/10 disabled:opacity-40"
            >
              MAX
            </button>
          </div>
          <Button
            onClick={handleWithdraw}
            disabled={!valid || withdrawing}
            data-testid="earnings-withdraw-btn"
            className="bg-ink-950 text-white hover:bg-ink-800"
          >
            {withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4" />}
            Withdraw
          </Button>
        </div>
      </div>

      {txs.length > 0 && (
        <div className="mt-5 pt-5 border-t border-amber-300/40">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-600" />
            Recent tips received
          </p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
            {txs.slice(0, 8).map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between text-xs bg-white/70 rounded-lg px-3 py-2"
                data-testid={`earnings-tx-${t.id}`}
              >
                <span className="text-ink-700">
                  <strong className="text-ink-950">{t.from_user_name || "Someone"}</strong> tipped you
                </span>
                <span className="font-bold text-amber-700 inline-flex items-center gap-1">
                  <Coins className="w-3 h-3" />
                  +{t.amount}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorEarningsCard;
